import { ReactNode } from 'react';

interface PageContainerProps {
  children: ReactNode;
  className?: string;
}

/**
 * THE page-level wrapper. Every page mounts its content through this component —
 * list, detail, create and admin alike — so the distance from the app header to
 * the first card is decided in ONE place instead of in a utility string per page.
 *
 * Why this exists as a component and not as a copied class string: on 2026-09-01
 * a census of all 36 page wrappers found THREE different top edges in the estate —
 *
 *   `tw-p-6`            24px top / 24px bottom   4 pages (vendor, customer, contact, category detail)
 *   `tw-px-6 tw-pb-8`    0px top / 32px bottom  28 pages (product detail, SO/PO, finance, pricing, scn, all lists)
 *   this component      32px top / 32px bottom   4 pages (OCC board, reports, system governance, taxonomy admin)
 *
 * — so the product detail page had NO top padding at all and its breadcrumb sat
 * flush against the app header, while vendor's had 24px of air. The component
 * that exists to settle the question was itself a fourth opinion, adopted by 4
 * pages out of 36.
 *
 * The rung is 24px on EVERY edge because VENDOR IS THE REFERENCE SURFACE and the
 * acceptance test for this change is a ZERO-PIXEL DIFF on vendor. A first cut of
 * this shell used 24 top / 32 bottom (the estate's majority bottom rung); that
 * moved vendor's bottom edge 24 -> 32 and would have failed its own acceptance
 * test, so it was withdrawn in favour of matching vendor exactly. Consequence,
 * declared rather than discovered: the six detail pages that carried a 32px
 * bottom lose 8px of scroll clearance, and the four admin pages that already
 * mounted this component move 32 -> 24 on both edges.
 *
 * Guard: src/components/common/__tests__/pageContainer.test.ts — reds if a page
 * hand-rolls a page-level padding wrapper instead of mounting this.
 *
 * @param children - Page content
 * @param className - Additional CSS classes (e.g. a page-specific max-width)
 */
const PageContainer = ({ children, className = '' }: PageContainerProps) => {
  return <div className={`tw-p-6 ${className}`}>{children}</div>;
};

export default PageContainer;
